import { useState, useEffect } from "react";
import { Star, Trash2, Flag, Eye, ThumbsUp, ThumbsDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetch = () => { setLoading(true); axios.get(`${API}/admin/reviews`, { withCredentials: true }).then(r => { setReviews(r.data.reviews || []); setTotal(r.data.total || 0); }).finally(() => setLoading(false)); };
  useEffect(() => { fetch(); }, []);

  const del = async (id) => { if (!window.confirm("Delete this review?")) return; await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true }); toast.success("Deleted"); fetch(); };

  const avg = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const dist = [5,4,3,2,1].map(s => ({ star: s, count: reviews.filter(r => r.rating === s).length }));

  return (
    <div data-testid="admin-reviews-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">Reviews & Ratings ({total})</h1>

      <div className="grid lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border text-center">
          <p className="text-4xl font-bold text-slate-800">{avg}</p>
          <div className="flex justify-center gap-0.5 my-2">{[1,2,3,4,5].map(s => <Star key={s} className={`w-4 h-4 ${s <= Math.round(avg) ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div>
          <p className="text-xs text-slate-500">{total} total reviews</p>
        </div>
        <div className="lg:col-span-3 bg-white rounded-2xl p-5 shadow-sm border">
          <p className="text-sm font-semibold text-slate-800 mb-3">Rating Distribution</p>
          {dist.map(d => (
            <div key={d.star} className="flex items-center gap-3 mb-1.5">
              <span className="text-xs font-medium w-6">{d.star}</span><Star className="w-3 h-3 fill-amber-400 text-amber-400" />
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${total > 0 ? (d.count / total * 100) : 0}%` }} /></div>
              <span className="text-xs text-slate-500 w-8">{d.count}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b bg-slate-50"><th className="p-4">Product</th><th className="p-4">User</th><th className="p-4">Rating</th><th className="p-4">Review</th><th className="p-4">Date</th><th className="p-4">Actions</th></tr></thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => <tr key={i}><td colSpan={6} className="p-4"><div className="h-10 skeleton-loading rounded" /></td></tr>) :
            reviews.map(r => (
              <tr key={r.review_id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="p-4 text-xs font-medium text-slate-800">{r.product_id}</td>
                <td className="p-4 text-xs">{r.user_name}</td>
                <td className="p-4"><div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-3 h-3 ${s <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div></td>
                <td className="p-4 text-xs text-slate-600 max-w-xs truncate">{r.text}</td>
                <td className="p-4 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                <td className="p-4"><div className="flex gap-1"><button onClick={() => setSelected(r)} className="p-1.5 hover:bg-cyan-50 rounded-lg text-slate-500"><Eye className="w-3 h-3" /></button><button onClick={() => del(r.review_id)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-500"><Trash2 className="w-3 h-3" /></button></div></td>
              </tr>
            ))}
            {!loading && reviews.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No reviews yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Review Detail</DialogTitle></DialogHeader>
          {selected && (<div className="space-y-3"><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-xs font-bold text-cyan-700">{selected.user_name?.[0]}</div><div><p className="text-sm font-semibold">{selected.user_name}</p><p className="text-xs text-slate-500">Product: {selected.product_id}</p></div></div><div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`w-5 h-5 ${s <= selected.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />)}</div><p className="text-sm text-slate-700 bg-sky-50 rounded-xl p-4">{selected.text}</p><p className="text-xs text-slate-400">{selected.created_at ? new Date(selected.created_at).toLocaleString() : ""}</p></div>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
